import { useState } from 'react';
import { Check, CircleMinus, Clock, X, type LucideIcon } from 'lucide-react';
import { Card } from '@shared/ui/containers/Card';
import { Button } from '@shared/ui/primitives/Button';
import { cn } from '@shared/lib/cn';
import { DetailRow } from './PageShell';
import { ListSheet } from './visitor-access/ListSheet';
import { Block, Row } from './visitor-access/Row';
import type { PersonVisit } from './memberContactsApi';
import { dateOf, dayOf, lastMoment, timeOf } from './visitorAccessFormat';
import { BUTTON_SECONDARY, CARD, ICON_CHIP, ROW_DIVIDER } from './visitorAccessStyles';

/** How many visits the page lists before "Show all visits". */
const FEW = 4;

const LIST_CARD = cn(CARD, 'overflow-hidden');

type VisitKind = 'arrived' | 'upcoming' | 'missed' | 'cancelled';

/** Each outcome's icon; a visit that happened, or still can, gets the brand tint. */
const KIND_ICON: Record<VisitKind, { icon: LucideIcon; tile: string }> = {
  arrived: { icon: Check, tile: ICON_CHIP },
  upcoming: { icon: Clock, tile: ICON_CHIP },
  missed: { icon: CircleMinus, tile: cn(ICON_CHIP, 'bg-sunken text-ink-muted') },
  cancelled: { icon: X, tile: cn(ICON_CHIP, 'bg-sunken text-ink-muted') },
};

interface PersonVisitsProps {
  /** The visits loaded so far, newest first. */
  visits: PersonVisit[];
  /** How many visits the person has in all. */
  total: number;
  /** Loads the next page of visits; absent once every visit is loaded. */
  onShowMore?: () => void;
  loadingMore: boolean;
}

/**
 * A person's visits: the latest few on the page, every one in the All visits drawer, and what
 * happened on each in its own drawer.
 */
export function PersonVisits({ visits, total, onShowMore, loadingMore }: PersonVisitsProps): JSX.Element {
  const [allOpen, setAllOpen] = useState(false);
  // The visit stays set while its drawer slides away, so the drawer never empties mid-close.
  const [visit, setVisit] = useState<PersonVisit | null>(null);
  const [visitOpen, setVisitOpen] = useState(false);

  const list = (shown: PersonVisit[]) => (
    <div className={LIST_CARD}>
      {shown.map((item) => (
        <VisitRow
          key={item.access_invite_id}
          visit={item}
          onOpen={() => {
            setVisit(item);
            setVisitOpen(true);
          }}
        />
      ))}
    </div>
  );

  return (
    <Block label={`Visits · ${total}`}>
      {/* 1. The latest few, and the drawer with all of them, loading older ones on request. */}
      {list(visits.slice(0, FEW))}
      {total > FEW ? (
        <Button variant="secondary" width="full" className={BUTTON_SECONDARY} onClick={() => setAllOpen(true)}>
          Show all visits
        </Button>
      ) : null}

      <ListSheet open={allOpen} onOpenChange={setAllOpen} title="All visits">
        <div className="flex flex-col gap-3">
          {list(visits)}
          {onShowMore ? (
            <Button variant="secondary" width="full" className={BUTTON_SECONDARY} loading={loadingMore} onClick={onShowMore}>
              Show more
            </Button>
          ) : null}
        </div>
      </ListSheet>

      {/* 2. One visit, opened from either list. */}
      <ListSheet open={visitOpen} onOpenChange={setVisitOpen} title={visit ? dayOf(visitDay(visit)) : ''}>
        {visit ? <VisitDetails visit={visit} /> : null}
      </ListSheet>
    </Block>
  );
}

function VisitRow({ visit, onOpen }: { visit: PersonVisit; onOpen: () => void }): JSX.Element {
  const { icon: Icon, tile } = KIND_ICON[visitKind(visit)];
  return (
    <Row
      className={ROW_DIVIDER}
      leading={
        <span className={tile}>
          <Icon className="h-4.5 w-4.5" strokeWidth={2} />
        </span>
      }
      title={dayOf(visitDay(visit))}
      line={visitOutcome(visit)}
      chevron
      onClick={onOpen}
    />
  );
}

/** What happened on a visit, in order, then the PIN's own details. */
export function VisitDetails({ visit }: { visit: PersonVisit }): JSX.Element {
  return (
    <VisitStory
      steps={visitSteps(visit)}
      details={[
        ['Type', visit.entry_type_name],
        ['Property', visit.location_name],
        ['PIN works', pinWindow(visit)],
      ]}
    />
  );
}

export interface VisitStep {
  label: string;
  meta: string;
  done: boolean;
}

/** A visit drawer's body: the steps as a timeline, then label and value rows. */
export function VisitStory({ steps, details }: { steps: VisitStep[]; details: [string, string | null][] }): JSX.Element {
  return (
    <div className="flex flex-col gap-4">
      {steps.length ? (
        <Card noGrain className={cn(CARD, 'p-4')}>
          <ol className="m-0 flex list-none flex-col p-0">
            {steps.map((step, index) => {
              const last = index === steps.length - 1;
              return (
                <li key={`${step.label}-${index}`} className="grid grid-cols-[0.75rem_minmax(0,1fr)] gap-x-3.5">
                  {/* Bead and stem share one column, so the line runs through every bead. */}
                  <div className="flex flex-col items-center">
                    <span
                      className={cn('mt-1.5 h-3 w-3 shrink-0 rounded-pill', step.done ? 'bg-primary-600' : 'bg-hairline-strong')}
                      aria-hidden
                    />
                    {last ? null : <span className="w-0.5 flex-1 bg-hairline" aria-hidden />}
                  </div>
                  <div className={cn(!last && 'pb-4')}>
                    <p className="m-0 text-[15px] font-semibold text-ink">{step.label}</p>
                    <p className="m-0 mt-0.5 text-[13px] text-ink-secondary">{step.meta}</p>
                  </div>
                </li>
              );
            })}
          </ol>
        </Card>
      ) : null}

      {details.length ? (
        <Card noGrain className={cn(CARD, 'flex flex-col px-3.5 py-0')}>
          {details.map(([label, value]) => (
            <DetailRow key={label} label={label} value={value} />
          ))}
        </Card>
      ) : null}
    </div>
  );
}

/** The day a visit is for: when its PIN starts working, else when it was made. */
export function visitDay(visit: PersonVisit): string {
  return visit.valid_from ?? visit.created_at;
}

function visitKind(visit: PersonVisit): VisitKind {
  if (visit.arrived_at) return 'arrived';
  if (visit.status === 'cancelled') return 'cancelled';
  const lapsed = visit.status === 'expired' || (visit.valid_until !== null && new Date(visit.valid_until) < new Date());
  return lapsed ? 'missed' : 'upcoming';
}

/** When they left, only if that was after their latest arrival: a PIN that lets them back in can leave an older exit. */
function leftAt(visit: PersonVisit): string | null {
  return visit.left_at && new Date(visit.left_at) > new Date(visit.arrived_at!) ? visit.left_at : null;
}

/** How a visit ended, or that it is still to come, in one line. */
function visitOutcome(visit: PersonVisit): string {
  switch (visitKind(visit)) {
    case 'arrived': {
      const left = leftAt(visit);
      return `Came in ${timeOf(visit.arrived_at!)}${left ? ` · Left ${timeOf(left)}` : ''}`;
    }
    case 'cancelled':
      return 'Cancelled';
    case 'missed':
      return 'Did not arrive';
    case 'upcoming':
      return 'Not arrived yet';
  }
}

/** The visit's story, oldest first: what the app and the gate recorded. */
function visitSteps(visit: PersonVisit): VisitStep[] {
  const at = (when: string | Date) => `${dateOf(when)}, ${timeOf(when)}`;
  const steps = [{ label: 'PIN made', meta: at(visit.created_at), done: true }];

  switch (visitKind(visit)) {
    case 'arrived': {
      steps.push({ label: 'Came in', meta: at(visit.arrived_at!), done: true });
      const left = leftAt(visit);
      if (left) steps.push({ label: 'Left', meta: at(left), done: true });
      break;
    }
    case 'cancelled':
      steps.push({
        label: 'Cancelled',
        meta: visit.cancelled_at ? `${at(visit.cancelled_at)}. The PIN stopped working.` : 'The PIN stopped working.',
        done: true,
      });
      break;
    case 'missed':
      steps.push({ label: 'Not used', meta: 'The PIN ran out without being used at the gate.', done: false });
      break;
    case 'upcoming':
      steps.push({
        label: 'Not arrived yet',
        meta: visit.valid_until ? `The PIN works until ${at(lastMoment(visit.valid_until))}.` : 'The PIN is still working.',
        done: false,
      });
      break;
  }
  return steps;
}

/** "16 Oct, 06:00 to 23:59", naming the end's day too when it ends on another one. */
function pinWindow(visit: PersonVisit): string {
  if (!visit.valid_until) return 'No end date';
  const from = visitDay(visit);
  const end = lastMoment(visit.valid_until);
  const endDay = dateOf(end) === dateOf(from) ? '' : `${dateOf(end)}, `;
  return `${dateOf(from)}, ${timeOf(from)} to ${endDay}${timeOf(end)}`;
}
